import { useCallback } from 'react';
import { documentCache, getCacheKey } from '@/lib/documentCache';

export const useDocumentCache = () => {
  const getCachedThumbnail = useCallback((fileUrl: string): string | null => {
    return documentCache.get<string>(getCacheKey.thumbnail(fileUrl));
  }, []);

  const setCachedThumbnail = useCallback((fileUrl: string, thumbnailUrl: string) => {
    documentCache.set(getCacheKey.thumbnail(fileUrl), thumbnailUrl, 30 * 60 * 1000); // 30 minutes
  }, []);

  const getCachedDocument = useCallback(<T,>(id: string): T | null => {
    return documentCache.get<T>(getCacheKey.document(id));
  }, []);

  const setCachedDocument = useCallback(<T,>(id: string, document: T) => {
    documentCache.set(getCacheKey.document(id), document, 10 * 60 * 1000); // 10 minutes
  }, []);

  const getCachedFilterResults = useCallback(<T,>(filters: Record<string, any>): T | null => {
    return documentCache.get<T>(getCacheKey.filterResults(filters));
  }, []);

  const setCachedFilterResults = useCallback(<T,>(filters: Record<string, any>, results: T) => {
    documentCache.set(getCacheKey.filterResults(filters), results, 2 * 60 * 1000); // 2 minutes
  }, []);

  const getCachedPreview = useCallback((id: string): string | null => {
    return documentCache.get<string>(getCacheKey.preview(id));
  }, []);

  const setCachedPreview = useCallback((id: string, previewData: string) => {
    documentCache.set(getCacheKey.preview(id), previewData, 15 * 60 * 1000); // 15 minutes
  }, []);

  const clearCache = useCallback(() => {
    documentCache.clear();
  }, []);

  const invalidateDocument = useCallback((id: string) => {
    documentCache.delete(getCacheKey.document(id));
  }, []);

  return {
    getCachedThumbnail,
    setCachedThumbnail,
    getCachedDocument,
    setCachedDocument,
    getCachedFilterResults,
    setCachedFilterResults,
    getCachedPreview,
    setCachedPreview,
    clearCache,
    invalidateDocument,
  };
};
